<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { compareIdentifiers } from "@persolog/utils";
import { useToast } from "primevue/usetoast";
import ProgressSpinner from "primevue/progressspinner";
import Toast from "primevue/toast";
import ScrollTop from "primevue/scrolltop";
import ProductLanguageSelector from "../components/textDiffs/ProductLanguageSelector.vue";
import DiffLegend from "../components/textDiffs/DiffLegend.vue";
import DiffTable from "../components/textDiffs/DiffTable.vue";
import ReleaseConfirmationDialog from "../components/textDiffs/ReleaseConfirmationDialog.vue";
import EmptyState from "../components/textDiffs/EmptyState.vue";
import { LanguageManagementAPI } from "../api";
import type { DataItem, DiffFilterType, DiffRow, Product } from "../types/TextDiffsTypes";
import { useRoute, useRouter } from "vue-router";

const { t } = useI18n();
const toast = useToast();
const route = useRoute();
const router = useRouter();

// Data refs
const selectedProduct = ref<Product | null>(null);
const selectedLanguage = ref<string | null>(null);
const products = ref<Product[]>([]);
const loading = ref(false);
const stagingData = ref<DataItem[]>([]);
const productiveData = ref<DataItem[]>([]);
const showReleaseDialog = ref(false);
const showRenderedView = ref(false);
const activeFilters = ref<DiffFilterType[]>([]);

// helper to apply preselection once products are available
function applyPreselectionFromRoute() {
    const qpProduct = String(route.query.product || "");
    const qpLanguage = String(route.query.language || "");

    if (!qpProduct) return;

    // find the matching product object
    console.log(products.value);
    const p = products.value.find(p => p.value === qpProduct) || null;
    selectedProduct.value = p;

    // only set language if present and available for this product
    if (p && qpLanguage) {
        const hasLang = p.languages.some(l => l.value === qpLanguage);
        selectedLanguage.value = hasLang ? qpLanguage : null;
    } else {
        selectedLanguage.value = null;
    }

    // load data if both are set
    if (selectedProduct.value && selectedLanguage.value) {
        loadData(selectedProduct.value.value, selectedLanguage.value);
    }
}

// Helper function to update URL parameters
function updateUrlParameters() {
    const query: Record<string, string> = {};
    
    // Add product parameter
    if (selectedProduct.value) {
        query.product = selectedProduct.value.value;
    }
    
    // Add language parameter
    if (selectedLanguage.value) {
        query.language = selectedLanguage.value;
    }
    
    // Update URL without navigation
    router.replace({ query });
}

// Computed property to create merged diff data
const diffData = computed<DiffRow[]>(() => {
    if (!stagingData.value.length && !productiveData.value.length) {
        return [];
    }

    // Get all unique keys from both datasets
    const allKeys = new Set([...stagingData.value.map((item) => item.key), ...productiveData.value.map((item) => item.key)]);

    return Array.from(allKeys)
        .map((key) => {
            const stagingItem = stagingData.value.find((item) => item.key === key);
            const productiveItem = productiveData.value.find((item) => item.key === key);

            const stagingValue = stagingItem?.value || "";
            const productiveValue = productiveItem?.value || "";
            const isDifferent = stagingValue !== productiveValue;

            return {
                key,
                stagingValue,
                productiveValue,
                isDifferent,
                isOnlyInStaging: !!stagingItem && !productiveItem,
                isOnlyInProductive: !stagingItem && !!productiveItem,
            };
        })
        .sort((a, b) => compareIdentifiers(a.key, b.key));
});

// Filtered diff data based on active filters
const filteredDiffData = computed<DiffRow[]>(() => {
    if (activeFilters.value.length === 0) {
        return diffData.value;
    }

    return diffData.value.filter((row) => {
        const rowType = getRowType(row);
        return activeFilters.value.includes(rowType);
    });
});

function getRowType(row: DiffRow): DiffFilterType {
    if (row.isOnlyInStaging) return "added";
    if (row.isOnlyInProductive) return "removed";
    if (row.isDifferent) return "changed";
    return "unchanged";
}

// Functions
async function loadProducts() {
    try {
        loading.value = true;
        // Use the same endpoint as TextMaintenance to get all namespaces
        const productActivations = await LanguageManagementAPI.getAllNamespaces('STAGING');

        // Show all namespaces as they are, without any filtering or prefix removal
        products.value = productActivations
            .map(product => ({
                label: product.name,
                value: product.name,
                languages: product.languages.map(language => ({
                    label: language,
                    value: language,
                })),
            }))
            .sort((a, b) => compareIdentifiers(a.label, b.label));
    } catch (error) {
        console.error("Failed to load products:", error);
    } finally {
        loading.value = false;
    }
}

async function loadData(productId: string, language: string = "de") {
    try {
        loading.value = true;

        // Load staging data
        const stagingResponse = await LanguageManagementAPI.getLatestSnapshot("STAGING", productId, language);
        const stagingArray = stagingResponse.texts;
        stagingData.value = stagingArray.map((item: any) => ({
            key: item.identifier,
            value: item.text,
        }));

        // Load productive data
        const productiveResponse = await LanguageManagementAPI.getLatestSnapshot("PROD", productId, language);
        const productiveArray = productiveResponse.texts;
        productiveData.value = productiveArray.map((item: any) => ({
            key: item.identifier,
            value: item.text,
        }));

        console.log(`Successfully loaded data for namespace: ${productId}`);
    } catch (error) {
        console.error("Failed to load data:", error);
        stagingData.value = [];
        productiveData.value = [];
    } finally {
        loading.value = false;
    }
}

function onProductChange() {
    selectedLanguage.value = null;
    stagingData.value = [];
    productiveData.value = [];
    
    // Update URL parameters
    updateUrlParameters();
}

function onLanguageChange() {
    if (selectedProduct.value && selectedLanguage.value) {
        loadData(selectedProduct.value.value, selectedLanguage.value);
    } else {
        stagingData.value = [];
        productiveData.value = [];
    }
    
    // Update URL parameters
    updateUrlParameters();
}

function openReleaseDialog() {
    showReleaseDialog.value = true;
}

async function confirmRelease() {
    if (selectedProduct.value && selectedLanguage.value) {
        await LanguageManagementAPI.releaseSnapshot(selectedProduct.value.value, selectedLanguage.value);
        toast.add({
            severity: "success",
            summary: t("textDiffs.releaseSuccess"),
            detail: t("textDiffs.releaseSuccessDetail", { product: selectedProduct.value.value, language: selectedLanguage.value }),
            life: 5000,
        });
        await loadData(selectedProduct.value.value, selectedLanguage.value);
    }
    showReleaseDialog.value = false;
}

function cancelRelease() {
    showReleaseDialog.value = false;
}

function openTextMaintenance() {
    if (selectedProduct.value && selectedLanguage.value) {
        const query: Record<string, string> = {
            product: selectedProduct.value.value, // Use full namespace name
            language: selectedLanguage.value
        };
        
        router.push({
            name: 'TextMaintenance',
            query
        });
    }
}

function toggleRenderedView() {
    showRenderedView.value = !showRenderedView.value;
}

function toggleFilter(filterType: DiffFilterType) {
    const index = activeFilters.value.indexOf(filterType);
    if (index > -1) {
        activeFilters.value.splice(index, 1);
    } else {
        activeFilters.value.push(filterType);
    }
}

onMounted(async () => {
    await loadProducts();
    applyPreselectionFromRoute();
});

onUnmounted(() => {
    // Clean up if needed
});
</script>

<template>
    <div class="text-diffs-container">
        <!-- Header -->
        <div class="page-header mb-6">
            <h1 class="page-title" data-cy="text-diffs-title">{{ t("textDiffs.title") }}</h1>
            <p class="page-description" data-cy="text-diffs-description">
                {{ t("textDiffs.description") }}
            </p>
        </div>

        <!-- Product and Language Selection -->
        <ProductLanguageSelector
            v-model:selectedProduct="selectedProduct"
            v-model:selectedLanguage="selectedLanguage"
            :products="products"
            :loading="loading"
            :showRenderedView="showRenderedView"
            :hasData="filteredDiffData.length > 0"
            :showEdit="filteredDiffData.length > 0"
            @productChange="onProductChange"
            @languageChange="onLanguageChange"
            @toggleRenderedView="toggleRenderedView"
            @release="openReleaseDialog"
            @edit="openTextMaintenance"
            data-cy="text-diffs_product-language-selector"
        />

        <!-- Loading Spinner -->
        <div v-if="loading" class="loading-container" data-cy="text-diffs_loading-spinner">
            <ProgressSpinner />
            <p>{{ t("textDiffs.loading") }}</p>
        </div>

        <!-- Diff Comparison Table -->
        <div v-if="selectedProduct && selectedLanguage && diffData.length > 0" class="diff-section" data-cy="text-diffs_comparison-section">
            <div class="diff-header" data-cy="text-diffs_diff-header">
                <h3 class="diff-title" data-cy="text-diffs_diff-title">{{ t("textDiffs.comparing") }} {{ selectedProduct.value }}</h3>
                <DiffLegend 
                    :activeFilters="activeFilters" 
                    @toggleFilter="toggleFilter"
                    data-cy="text-diffs_legend"
                />
            </div>

            <DiffTable 
                :diffData="filteredDiffData" 
                :showRenderedView="showRenderedView"
                data-cy="text-diffs_table"
            />
        </div>

        <!-- Empty State -->
        <EmptyState 
            v-else-if="selectedProduct && selectedLanguage && filteredDiffData.length === 0"
            data-cy="text-diffs_empty-state"
        />

        <!-- Release Confirmation Dialog -->
        <ReleaseConfirmationDialog
            v-model:visible="showReleaseDialog"
            :selectedProduct="selectedProduct"
            :selectedLanguage="selectedLanguage"
            @confirm="confirmRelease"
            @cancel="cancelRelease"
            data-cy="text-diffs_release-dialog"
        />

        <!-- Toast for notifications -->
        <Toast />

        <!-- Scroll to top button -->
        <ScrollTop />

        <!-- Hidden element for testing purposes -->
        <div style="display: none" data-cy="scroll-to-top-button"></div>
    </div>
</template>

<style scoped>
.text-diffs-container {
    /* Color variables for diff states */
    --diff-unchanged-hover: #f3f4f6;

    --diff-changed-bg: #fef3c7;
    --diff-changed-text: #965000;
    --diff-changed-border: #fbbf24;
    --diff-changed-hover: #fcedb2;
    --diff-changed-text-bg: transparent;
    --diff-changed-hover-bg: #feeca5;

    --diff-added-bg: #dcfce7;
    --diff-added-text: #15803d;
    --diff-added-border: #4ade80;
    --diff-added-hover: #cfffe0;
    --diff-added-hover-bg: #4e9563;
    --diff-added-text-bg: transparent;

    --diff-removed-bg: #fee2e2;
    --diff-removed-text: #dc2626;
    --diff-removed-border: #fca5a5;
    --diff-removed-hover: #ffd4d4;
    --diff-removed-hover-bg: #c44343;
    --diff-removed-text-bg: transparent;

    --p-button-success-background: var(--p-orange-600);
    --p-button-success-border-color: var(--p-orange-600);
    --p-button-success-hover-background: var(--p-orange-700);
    --p-button-success-hover-border-color: var(--p-orange-700);
    --p-button-success-active-background: var(--p-orange-800);
    --p-button-success-active-border-color: var(--p-orange-800);
}

.diff-section {
    margin-top: 2rem;
}

.diff-header {
    margin-bottom: 3rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.diff-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
}

.loading-container {
    text-align: center;
    padding: 2rem 1rem;
    margin-top: 1.5rem;
}

@media (max-width: 768px) {
    .diff-header {
        flex-direction: column;
        align-items: stretch;
    }
}
</style>
